/**
 * Interaction System Types
 * 
 * Defines the interaction system for entity-to-entity and player-to-entity interactions.
 * Supports multiple interaction types: proximity, raycast, click, trigger zones, etc.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { IEntity } from "../entity/Entity.types";

/**
 * Types of interactions that can occur
 */
export enum InteractionTriggerType {
    /** Triggered when entities are within proximity range */
    PROXIMITY = "proximity",
    /** Triggered by raycast/looking at target */
    RAYCAST = "raycast",
    /** Triggered by clicking/pressing interact button */
    CLICK = "click",
    /** Triggered when entering a trigger zone */
    TRIGGER_ENTER = "trigger_enter",
    /** Triggered when exiting a trigger zone */
    TRIGGER_EXIT = "trigger_exit",
    /** Triggered by collision */
    COLLISION = "collision",
    /** Continuous interaction while in range */
    CONTINUOUS = "continuous",
    /** Automatic interaction (NPCs, AI) */
    AUTOMATIC = "automatic",
}

/**
 * Categories of interactable objects
 */
export enum InteractableCategory {
    /** Player character */
    PLAYER = "player",
    /** Non-player characters */
    NPC = "npc",
    /** Pickupable items */
    ITEM = "item",
    /** Containers (chests, boxes) */
    CONTAINER = "container",
    /** Doors, gates */
    DOOR = "door",
    /** Switches, levers, buttons */
    SWITCH = "switch",
    /** Readable objects (signs, books) */
    READABLE = "readable",
    /** Vehicles */
    VEHICLE = "vehicle",
    /** Environment objects */
    ENVIRONMENT = "environment",
    /** Custom category */
    CUSTOM = "custom",
}

/**
 * Interaction priority levels
 */
export enum InteractionPriority {
    LOWEST = 0,
    LOW = 25,
    NORMAL = 50,
    HIGH = 75,
    HIGHEST = 100,
    CRITICAL = 999,
}

/**
 * Result of an interaction
 */
export enum InteractionResult {
    /** Interaction succeeded */
    SUCCESS = "success",
    /** Interaction failed */
    FAILED = "failed",
    /** Interaction blocked/prevented */
    BLOCKED = "blocked",
    /** Interaction in progress (async) */
    PENDING = "pending",
    /** No interaction occurred */
    NONE = "none",
}

/**
 * Interactable component configuration
 */
export interface InteractableConfig {
    /** Whether this entity can be interacted with */
    enabled: boolean;
    /** Category of the interactable */
    category: InteractableCategory;
    /** Types of interactions this supports */
    triggerTypes: InteractionTriggerType[];
    /** Interaction range (for proximity) */
    interactionRadius: number;
    /** Priority when multiple interactions are possible */
    priority: InteractionPriority;
    /** Display name for UI */
    displayName?: string;
    /** Interaction prompt (e.g., "Press E to interact") */
    interactionPrompt?: string;
    /** Highlight color when hoverable */
    highlightColor?: { r: number; g: number; b: number; a: number };
    /** Whether to highlight on hover/proximity */
    highlightOnHover: boolean;
    /** Whether to show UI prompt */
    showPrompt: boolean;
    /** Cooldown between interactions (ms) */
    cooldown: number;
    /** Whether only one entity can interact at a time */
    exclusiveInteraction: boolean;
    /** Custom collision mesh for interaction detection */
    collisionMesh?: AbstractMesh;
    /** Tags for filtering interactions */
    tags: string[];
    /** Custom data */
    metadata?: Record<string, unknown>;
}

/**
 * Interactor component configuration (for entities that can interact)
 */
export interface InteractorConfig {
    /** Whether this entity can initiate interactions */
    enabled: boolean;
    /** Raycast distance for raycast interactions */
    raycastDistance: number;
    /** Proximity detection radius */
    proximityRadius: number;
    /** Maximum number of simultaneous interactions */
    maxSimultaneousInteractions: number;
    /** Categories this interactor can interact with */
    allowedCategories: InteractableCategory[];
    /** Categories to ignore */
    ignoredCategories: InteractableCategory[];
    /** Tags required on interactable */
    requiredTags: string[];
    /** Tags to ignore */
    ignoredTags: string[];
    /** Auto-interact with high priority targets */
    autoInteract: boolean;
    /** Custom data */
    metadata?: Record<string, unknown>;
}

/**
 * Interaction event data
 */
export interface InteractionEvent {
    /** Type of trigger that caused this interaction */
    triggerType: InteractionTriggerType;
    /** The entity initiating the interaction */
    initiator: IEntity;
    /** The entity being interacted with */
    target: IEntity;
    /** World position where interaction occurred */
    position: Vector3;
    /** Distance between entities */
    distance: number;
    /** Hit point (for raycast) */
    hitPoint?: Vector3;
    /** Hit normal (for raycast) */
    hitNormal?: Vector3;
    /** Timestamp of the interaction */
    timestamp: number;
    /** Result of the interaction */
    result: InteractionResult;
    /** Custom data passed with interaction */
    data?: Record<string, unknown>;
}

/**
 * Interaction handler function
 */
export type InteractionHandler = (event: InteractionEvent) => InteractionResult | Promise<InteractionResult>;

/**
 * Proximity detection result
 */
export interface ProximityResult {
    entity: IEntity;
    distance: number;
    isInRange: boolean;
    wasInRange: boolean;
    justEntered: boolean;
    justExited: boolean;
}

/**
 * Raycast interaction result
 */
export interface RaycastInteractionResult {
    hit: boolean;
    entity?: IEntity;
    distance: number;
    hitPoint?: Vector3;
    hitNormal?: Vector3;
    mesh?: AbstractMesh;
}

/**
 * Interaction state for an entity
 */
export interface InteractionState {
    /** Currently interacting with */
    currentInteractions: Set<string>;
    /** Entities in proximity */
    proximityEntities: Map<string, ProximityResult>;
    /** Currently highlighted entity */
    highlightedEntity: string | null;
    /** Is interaction on cooldown */
    onCooldown: boolean;
    /** Cooldown end time */
    cooldownEndTime: number;
    /** Is currently being interacted with */
    isBeingInteractedWith: boolean;
    /** Who is interacting */
    interactingEntity: string | null;
}

/**
 * Interaction filter function
 */
export type InteractionFilter = (initiator: IEntity, target: IEntity) => boolean;

/**
 * Interaction manager configuration
 */
export interface InteractionManagerConfig {
    /** Global interaction enabled state */
    enabled: boolean;
    /** Default proximity check interval (ms) */
    proximityCheckInterval: number;
    /** Default raycast distance */
    defaultRaycastDistance: number;
    /** Maximum entities to track in proximity */
    maxProximityEntities: number;
    /** Show debug visualizations */
    debug: boolean;
    /** Global interaction filters */
    globalFilters: InteractionFilter[];
    /** Default highlight color */
    defaultHighlightColor: { r: number; g: number; b: number; a: number };
    /** Auto-show interaction prompts */
    autoShowPrompts: boolean;
}

/**
 * Interaction system events
 */
export type InteractionEventType =
    | "interaction_start"
    | "interaction_end"
    | "interaction_success"
    | "interaction_failed"
    | "proximity_enter"
    | "proximity_exit"
    | "highlight_start"
    | "highlight_end"
    | "cooldown_start"
    | "cooldown_end";

/**
 * Interaction system event callback
 */
export type InteractionEventCallback = (event: InteractionEvent, eventType: InteractionEventType) => void;
